import type React from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';
import { DetailPanelOutlet, DetailPanelProvider } from 'app/shared/halo/components/ui/DetailPanel';
import FeedCard from 'app/shared/modules/FeedCard';
import FeedCardSkeleton from 'app/shared/modules/FeedCard/FeedCardSkeleton';
import ChevronUp from 'app/shared/modules/ViewModeToggle/ChevronUp';
import { ChevronLeftIcon, ListIcon } from 'app/shared/halo/components/ui/icons';
import { Button } from 'app/shared/halo/components/ui/Button';
import { useViewMode } from 'app/shared/hooks/useViewMode';
import { openShortlistSheet } from 'app/shared/halo/stores/shortlistSheetStore';
import ChevronDown from './ChevronDown';
import { emitTrackImpressionForPropertyCardEvent } from 'app/shared/models/Clickstream/ListingCardClickstreamEvents';
import { emitTrackToggleViewModeEvent } from 'app/shared/models/Clickstream/ListingsClickstreamEvents';
import type { ListingDetails } from 'app/types/listingDetails.type';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import {
  getSavedFeedIndex,
  saveSrpScrollPosition,
  useSrpFeedScrollRestore,
} from 'app/shared/hooks/useSrpScrollRestore';
import { useSnapScroll } from 'app/shared/hooks/useSnapScroll';
import {
  backToListButton,
  backToListChevron,
  feedContainer,
  feedCounterBold,
  feedCounterText,
  feedHeader,
  feedHeaderRow,
  feedScroller,
  feedSlide,
  navButton,
  navIcon,
  rightRail,
  srOnly,
} from './feedView.styles';

type FeedViewProps = {
  listings: Array<ListingDetails>;
};

const MD_BREAKPOINT = 768;

const FeedView = ({ listings }: FeedViewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollerElRef = useRef<HTMLDivElement | null>(null);
  const [feedHeight, setFeedHeight] = useState<string | null>(null);
  const dispatch = useDispatch();
  const [, setViewMode] = useViewMode();
  const isMobile = useSelector((state: RootReduxState) => state.app.device.screenWidth === 'sm');

  const [savedInitialIndex] = useState(() => getSavedFeedIndex(listings.length));

  const {
    scrollerRef,
    setSlideRef,
    activeIndex: currentIndex,
    scrollToIndex,
    scrollToNext: goToNext,
    scrollToPrevious: goToPrevious,
  } = useSnapScroll({
    direction: 'y',
    itemCount: listings.length,
    initialIndex: savedInitialIndex,
  });

  const combinedScrollerRef = useCallback(
    (el: HTMLDivElement | null) => {
      scrollerElRef.current = el;
      scrollerRef(el);
    },
    [scrollerRef],
  );

  useSrpFeedScrollRestore({ listingsCount: listings.length, feedHeight, scrollToIndex, scrollerElRef, isMobile });

  // Measure container offset to calculate available viewport height.
  // On mobile, position: fixed with inset handles sizing so no inline height needed.
  // On desktop, also lock the scrollable sidebar to prevent scrolling past the feed.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let scrollParent: HTMLElement | null = null;

    if (window.innerWidth >= MD_BREAKPOINT) {
      // Find the nearest scrollable ancestor (the sidebar) and lock it so
      // users can't scroll past the feed to the SRP content below.
      let el: HTMLElement | null = container.parentElement;
      while (el) {
        const { overflowY } = getComputedStyle(el);
        if (overflowY === 'auto' || overflowY === 'scroll') {
          scrollParent = el;
          break;
        }
        el = el.parentElement;
      }
      if (scrollParent) {
        scrollParent.scrollTop = 0;
        scrollParent.style.overflowY = 'hidden';
      }
    }

    const updateHeight = () => {
      if (window.innerWidth < MD_BREAKPOINT) {
        setFeedHeight(null);
        return;
      }
      const rect = container.getBoundingClientRect();
      setFeedHeight(`calc(100vh - ${Math.round(rect.top)}px)`);
    };

    updateHeight();
    let resizeTimer: ReturnType<typeof setTimeout>;
    const debouncedUpdate = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateHeight, 150);
    };
    window.addEventListener('resize', debouncedUpdate);
    return () => {
      window.removeEventListener('resize', debouncedUpdate);
      clearTimeout(resizeTimer);
      if (scrollParent) {
        scrollParent.style.overflowY = '';
      }
    };
  }, []);

  const handleBeforeNavigate = useCallback(() => {
    saveSrpScrollPosition({ type: 'feed', index: currentIndex });
  }, [currentIndex]);

  const seenListingsRef = useRef(new Set<string>());
  const currentListing = listings[currentIndex];

  // Sync active listing to map marker and track impression
  useEffect(() => {
    if (!currentListing) return;

    dispatch(ListingEngineActions.setActiveMarkerMaloneLotId(currentListing.maloneLotIdEncoded));

    const listingId = currentListing.maloneLotIdEncoded;
    if (listingId && !seenListingsRef.current.has(listingId)) {
      seenListingsRef.current.add(listingId);
      emitTrackImpressionForPropertyCardEvent({
        listing: currentListing,
        triggerLocation: 'search_results_feed_halo',
      });
    }

    return () => {
      dispatch(ListingEngineActions.setActiveMarkerMaloneLotId(null));
    };
  }, [currentListing, dispatch]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        goToNext();
      }
    },
    [goToPrevious, goToNext],
  );

  const handleBackToList = useCallback(() => {
    emitTrackToggleViewModeEvent({ targetView: 'list' });
    setViewMode('grid');
  }, [setViewMode]);

  if (listings.length === 0) return null;

  const paddedCurrent = String(currentIndex + 1).padStart(2, '0');
  const paddedTotal = String(listings.length).padStart(2, '0');

  return (
    <DetailPanelProvider isMobile={isMobile} scrollContainerRef={containerRef}>
      <div
        ref={containerRef}
        tabIndex={-1}
        role="region"
        aria-label="Listing feed"
        onKeyDown={handleKeyDown}
        className={feedContainer}
        style={feedHeight ? { height: feedHeight } : undefined}
      >
        <div className={feedHeader}>
          <div className={feedHeaderRow}>
            <button type="button" className={backToListButton} onClick={handleBackToList}>
              <span className={backToListChevron}>
                <ChevronLeftIcon />
              </span>
              Back to list
            </button>
            <Button
              shape="circle"
              tone="neutral"
              emphasis="outlined"
              size="md"
              onClick={openShortlistSheet}
              aria-label="View saved listings"
            >
              <ListIcon />
            </Button>
          </div>
          <p className={feedCounterText} aria-hidden="true">
            <span className={feedCounterBold}>
              {paddedCurrent} of {paddedTotal}
            </span>{' '}
            Rentals available
          </p>
        </div>

        <div ref={combinedScrollerRef} className={feedScroller}>
          {listings.map((listing, i) => (
            <div key={listing.aliasEncoded || i} ref={setSlideRef(i)} className={feedSlide}>
              {Math.abs(i - currentIndex) <= 1 ? (
                <FeedCard listing={listing} isActive={i === currentIndex} onBeforeNavigate={handleBeforeNavigate} />
              ) : (
                <FeedCardSkeleton />
              )}
            </div>
          ))}
        </div>

        <div className={rightRail}>
          <button
            type="button"
            className={navButton}
            onClick={goToPrevious}
            disabled={currentIndex === 0}
            aria-label="Previous listing"
          >
            <ChevronUp className={navIcon} />
          </button>
          <button
            type="button"
            className={navButton}
            onClick={goToNext}
            disabled={currentIndex === listings.length - 1}
            aria-label="Next listing"
          >
            <ChevronDown className={navIcon} />
          </button>
        </div>

        <div role="status" aria-live="polite" className={srOnly}>
          Listing {currentIndex + 1} of {listings.length}
        </div>

        {!isMobile && <DetailPanelOutlet />}
      </div>
    </DetailPanelProvider>
  );
};

export default FeedView;
